import { ApiError } from '~/api/errors';

import type { ArchiveLiveness } from '../types/list-archive.types';

/** Why an archive / unarchive call failed, per the Archive API contract. */
export type ArchiveRefusalKind =
  /** `JOB_STILL_LIVE` — the run is executing (`liveness` OPEN) or the probe
   *  couldn't tell (`liveness` UNKNOWN). Only OPEN has something to Stop. */
  | 'still-live'
  /** `JOB_DELIVERING` — approved, and a decoupled delivery is writing to the
   *  connector right now. Nothing to stop; it settles itself. */
  | 'delivering'
  /** `EXPERIMENT_WORK_IN_FLIGHT` — an experiment sub-run is executing. Offer Stop. */
  | 'experiment-in-flight'
  /** `ARCHIVE_STATE_CHANGED` — someone moved the flag between read and write.
   *  Not a user error: re-read and retry silently. */
  | 'state-changed'
  /** 404 — the row is gone (or not in the caller's subsidiary). */
  | 'not-found'
  /** The tenant-less 400 (a super-admin token with no subsidiary in scope). */
  | 'no-subsidiary'
  | 'unknown';

export interface ArchiveRefusal {
  kind: ArchiveRefusalKind;
  /** From `fields.liveness[0]`; `null` when the envelope didn't carry it. */
  liveness: ArchiveLiveness | null;
  /** From `fields.workflowId[0]` — the live workflow, for a Stop deep-link. */
  workflowId: string | null;
  /** Server prose (`error.details` / legacy `errors[0]`), for fallback toasts. */
  message: string;
}

/** `errors[0]` prefix of the tenant-less 400. It shares `message: "Validation
 *  failed"` with the malformed-body 400, so only the detail can tell them apart. */
const NO_SUBSIDIARY_PREFIX = 'Active tenant required';

const CODE_TO_KIND: Record<string, ArchiveRefusalKind> = {
  JOB_STILL_LIVE: 'still-live',
  JOB_DELIVERING: 'delivering',
  EXPERIMENT_WORK_IN_FLIGHT: 'experiment-in-flight',
  ARCHIVE_STATE_CHANGED: 'state-changed',
};

const LIVENESS_VALUES: ReadonlySet<string> = new Set<ArchiveLiveness>([
  'OPEN',
  'UNKNOWN',
  'CLOSED',
  'NOT_PROBED',
]);

/**
 * Classifies a failed archive toggle so the UI can act on it. Branches on the
 * envelope's `error.code` (surfaced as `ApiError.serverCode`) and reads
 * `fields.liveness` / `fields.workflowId` — never on the prose, which changes.
 * Falls back to the legacy shape (a 409 without a code is treated as still-live)
 * so a pre-contract backend still gets a sensible message.
 */
export function describeArchiveRefusal(err: unknown): ArchiveRefusal {
  if (!(err instanceof ApiError)) {
    return { kind: 'unknown', liveness: null, workflowId: null, message: '' };
  }
  const message = err.message;
  const livenessRaw = err.fields?.liveness?.[0];
  const liveness =
    livenessRaw && LIVENESS_VALUES.has(livenessRaw) ? (livenessRaw as ArchiveLiveness) : null;
  const workflowId = err.fields?.workflowId?.[0] ?? null;

  const coded = err.serverCode ? CODE_TO_KIND[err.serverCode] : undefined;
  if (coded) return { kind: coded, liveness, workflowId, message };
  if (err.statusCode === 409) return { kind: 'still-live', liveness, workflowId, message };
  if (err.statusCode === 404) return { kind: 'not-found', liveness, workflowId, message };
  if (err.statusCode === 400 && (err.messages[0] ?? '').startsWith(NO_SUBSIDIARY_PREFIX)) {
    return { kind: 'no-subsidiary', liveness, workflowId, message };
  }
  return { kind: 'unknown', liveness, workflowId, message };
}

/** Kind-only shorthand for callers that just need to branch. */
export function classifyArchiveError(err: unknown): ArchiveRefusalKind {
  return describeArchiveRefusal(err).kind;
}
